import type { OrganizationRole } from '@octave/contracts';
import type { GlobalRole } from '@/lib/auth/token';

export const roleMapping: Record<string, string> = {
  ADMIN: 'Administrateur',
  EDITOR: 'Éditeur',
  VIEWER: 'Lecteur',
};

export type Permission = 'read' | 'write' | 'delete' | 'invite' | 'manage';

// Permissions par rôle
export const rolePermissions = {
  ADMIN: ['read', 'write', 'delete', 'invite', 'manage'],
  EDITOR: ['read', 'write', 'delete', 'manage'],
  VIEWER: ['read'],
} as const satisfies Record<OrganizationRole, readonly Permission[]>;

// The per-organization role IS the contract's vocabulary — one source.
export type Role = OrganizationRole;

// Fonction pour vérifier si un rôle a une permission.
// `userRole` est le rôle global de plateforme (ADMIN/CLIENT, cf. le payload
// du JWT) ; `userOrganizationRole` est le rôle par organisation.
export function hasPermission(
  userRole: GlobalRole | null,
  userOrganizationRole: Role | null,
  permission: Permission
): boolean {
  // Si userRole est ADMIN, toutes les permissions sont accordées
  if (userRole === 'ADMIN') return true;

  // Sinon, on vérifie les permissions basées sur le userOrganizationRole
  if (!userOrganizationRole) return false;
  return (
    (rolePermissions[userOrganizationRole] as readonly Permission[])?.includes(
      permission
    ) || false
  );
}

// Fonction pour vérifier si un utilisateur peut effectuer des actions d'écriture
export function canWrite(
  userRole: GlobalRole | null,
  userOrganizationRole: Role | null
): boolean {
  return hasPermission(userRole, userOrganizationRole, 'write');
}

// Fonction pour vérifier si un utilisateur peut effectuer des actions de suppression
export function canDelete(
  userRole: GlobalRole | null,
  userOrganizationRole: Role | null
): boolean {
  return hasPermission(userRole, userOrganizationRole, 'delete');
}

// Fonction pour vérifier si un utilisateur peut inviter d'autres utilisateurs
export function canInvite(
  userRole: GlobalRole | null,
  userOrganizationRole: Role | null
): boolean {
  return hasPermission(userRole, userOrganizationRole, 'invite');
}

// Fonction pour vérifier si un utilisateur peut gérer l'organisation
export function canManage(
  userRole: GlobalRole | null,
  userOrganizationRole: Role | null
): boolean {
  return hasPermission(userRole, userOrganizationRole, 'manage');
}
